import { normalizePlanningRevisionEvidence } from "../utils/planningRevisionDiagnostics.js";
import { normalizeMutationTransport, normalizeServerResponseTransport, normalizeCapturedMutationDiagnostic } from "../utils/mutationTransportDiagnostics.js";
import { normalizeOrderEditCapture } from "../utils/planningOrderEditDiagnostics.js";

export const DISPATCH_ALERT_CAPTURE_CONTEXT_SCHEMA_VERSION = 1;
export const DISPATCH_ALERT_CAPTURE_CONTEXT_MAX_BYTES = 16 * 1024;
export const DISPATCH_ALERT_CAPTURE_CONTEXT_MAX_IDENTIFIERS = 50;
export const DISPATCH_ALERT_CAPTURE_CONTEXT_MAX_DETAILS = 20;

const ORDER_ID_KEYS = [
  "affected_order_ids",
  "forced_preloaded_previous_day_order_ids",
  "moved_order_id",
  "moved_order_ids",
  "order_id",
  "order_ids",
  "reactivated_order_ids",
  "released_order_ids",
  "selected_order_ids",
  "suspended_order_ids",
];
const DELIVERY_ID_KEYS = [
  "affected_delivery_ids",
  "delivery_id",
  "delivery_ids",
  "moved_delivery_ids",
  "selected_delivery_ids",
  "suspended_group_member_ids",
];
const LOADING_STEP_ID_KEYS = ["group_step_ids", "loading_step_id", "loading_step_ids"];
const SENSITIVE_DETAIL_MARKERS = [
  "api_key",
  "apikey",
  "authorization",
  "body",
  "cookie",
  "corps de la requête",
  "corps de la réponse",
  "corps http",
  "diagnostic brut",
  "en-tête",
  "header",
  "mot de passe",
  "password",
  "payload",
  "raw diagnostic",
  "raw response",
  "raw_response",
  "secret",
  "stack",
  "token",
  "trace",
];

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function normalizeText(value, maxLength = 512) {
  if (typeof value !== "string") {
    return "";
  }
  return value.replace(/\s+/g, " ").trim().slice(0, maxLength);
}

function normalizeInteger(value, { min = 0, max = Number.MAX_SAFE_INTEGER } = {}) {
  const number = Number(value);
  return Number.isInteger(number) && number >= min && number <= max ? number : null;
}

function normalizeIdentifierCandidate(value) {
  if (typeof value === "string" || typeof value === "number") {
    return normalizeText(String(value), 255);
  }
  if (!isPlainObject(value)) {
    return "";
  }
  for (const key of ["id", "order_id", "delivery_id", "loading_step_id", "truck_id"]) {
    const identifier = normalizeText(value[key], 255);
    if (identifier) {
      return identifier;
    }
  }
  return "";
}

function normalizeIdentifierList(value, { additional = [] } = {}) {
  const values = [
    ...(Array.isArray(additional) ? additional : [additional]),
    ...(Array.isArray(value) ? value : value == null ? [] : [value]),
  ];
  const identifiers = [];
  for (const candidate of values) {
    const identifier = normalizeIdentifierCandidate(candidate);
    if (!identifier || identifiers.includes(identifier)) {
      continue;
    }
    identifiers.push(identifier);
    if (identifiers.length >= DISPATCH_ALERT_CAPTURE_CONTEXT_MAX_IDENTIFIERS) {
      break;
    }
  }
  return identifiers;
}

function collectIdentifiersByKeys(value, keys, additional = []) {
  const collected = [...(Array.isArray(additional) ? additional : [additional])];
  if (isPlainObject(value)) {
    keys.forEach((key) => {
      const candidate = value[key];
      if (Array.isArray(candidate)) {
        collected.push(...candidate);
      } else if (candidate != null) {
        collected.push(candidate);
      }
    });
  }
  const allIdentifiers = normalizeIdentifierList(collected);
  return {
    count: new Set(collected.map(normalizeIdentifierCandidate).filter(Boolean)).size,
    values: allIdentifiers,
  };
}

function normalizeTruck(value) {
  if (!isPlainObject(value)) {
    return null;
  }
  const id = normalizeText(value.id, 255);
  const label = normalizeText(value.label, 255);
  return id || label ? { ...(id ? { id } : {}), ...(label ? { label } : {}) } : null;
}

function normalizeDetails(value) {
  const details = [];
  for (const [index, candidate] of (Array.isArray(value) ? value : []).entries()) {
    if (!isPlainObject(candidate)) {
      continue;
    }
    const label = normalizeText(candidate.label, 160);
    const detailValue = normalizeText(candidate.value, 1024);
    if (!label || !detailValue) {
      continue;
    }
    const key = normalizeText(candidate.key, 120) || `detail-${index + 1}`;
    const keyAndLabel = `${key} ${label}`.toLocaleLowerCase("fr-CA");
    const normalizedValue = detailValue.toLocaleLowerCase("fr-CA");
    let isRawStructuredPayload = false;
    if (["{", "["].includes(detailValue[0])) {
      try {
        const parsedValue = JSON.parse(detailValue);
        isRawStructuredPayload = Boolean(parsedValue) && typeof parsedValue === "object";
      } catch {
        isRawStructuredPayload = false;
      }
    }
    if (
      isRawStructuredPayload
      || SENSITIVE_DETAIL_MARKERS.some((marker) => keyAndLabel.includes(marker))
      || [
        "authorization:",
        "bearer ",
        "password=",
        "secret=",
        "token=",
        "traceback (most recent call last)",
      ].some((marker) => normalizedValue.includes(marker))
    ) {
      continue;
    }
    const signature = `${key}\u0000${label}\u0000${detailValue}`;
    if (details.some((detail) => detail.signature === signature)) {
      continue;
    }
    details.push({ key, label, value: detailValue, signature });
    if (details.length >= DISPATCH_ALERT_CAPTURE_CONTEXT_MAX_DETAILS) {
      break;
    }
  }
  return details.map(({ signature: _signature, ...detail }) => detail);
}

function jsonByteLength(value) {
  const serialized = typeof value === "string" ? value : JSON.stringify(value);
  if (typeof TextEncoder === "function") {
    return new TextEncoder().encode(serialized).byteLength;
  }
  return unescape(encodeURIComponent(serialized)).length;
}

function fitContextToByteLimit(context) {
  const fitted = JSON.parse(JSON.stringify(context));
  while (jsonByteLength(fitted) > DISPATCH_ALERT_CAPTURE_CONTEXT_MAX_BYTES) {
    if (fitted.planning_revision?.events?.length) {
      fitted.planning_revision.events.shift();
      continue;
    }
    if (fitted.planning_revision?.revision_cause?.transitions) {
      fitted.planning_revision.revision_cause = { state: "untraced" };
      continue;
    }
    if (Array.isArray(fitted.details) && fitted.details.length) {
      fitted.details.pop();
      if (!fitted.details.length) {
        delete fitted.details;
      }
      continue;
    }
    const entities = isPlainObject(fitted.entities) ? fitted.entities : null;
    const lists = entities
      ? ["order_ids", "delivery_ids", "loading_step_ids"]
        .map((key) => entities[key])
        .filter((candidate) => Array.isArray(candidate) && candidate.length)
      : [];
    if (lists.length) {
      lists.sort((left, right) => right.length - left.length)[0].pop();
      continue;
    }
    const fields = [fitted.order_edit?.submission, fitted.order_edit?.canonical]
      .flatMap((item) => (item?.changed_fields || []).flatMap((change) => ["before", "after"].map((key) => ({ change, key }))));
    fields.sort((a, b) => (b.change[b.key]?.length || 0) - (a.change[a.key]?.length || 0));
    const longest = fields[0];
    if (longest && longest.change[longest.key]?.length > 40) {
      const characters = Array.from(longest.change[longest.key]);
      longest.change[longest.key] = characters.slice(0, Math.floor(characters.length / 2)).join("");
      longest.change[`${longest.key}_truncated`] = true;
      continue;
    }
    return null;
  }
  return fitted;
}

function findFirstNestedText(value, keys, depth = 0, seen = new Set()) {
  if (!value || depth > 5 || typeof value !== "object" || seen.has(value)) {
    return "";
  }
  seen.add(value);
  if (isPlainObject(value)) {
    for (const key of keys) {
      const text = normalizeText(value[key], 512);
      if (text) {
        return text;
      }
    }
  }
  for (const child of Object.values(value)) {
    if (child && typeof child === "object") {
      const found = findFirstNestedText(child, keys, depth + 1, seen);
      if (found) {
        return found;
      }
    }
  }
  return "";
}

function normalizePath(value) {
  const rawPath = normalizeText(value, 1024);
  if (!rawPath) {
    return "";
  }
  try {
    return new URL(rawPath, "http://grain.local").pathname.slice(0, 512);
  } catch {
    return rawPath.split("?")[0].slice(0, 512);
  }
}

const VALIDATION_FIELDS = new Set([
  "body", "query", "path", "employee_id", "expected_assignment_version", "expected_runtime_revision",
  "dispatch_date", "dispatch_period", "allow_transfer", "confirmed_versions", "truck_id", "truckId",
  "start_time", "max_work_hours", "capacity_tons", "scope", "placement", "draggedStepId", "draggedStepIds",
  "targetStepId", "parentLoadingId", "rebuild_mode", "protect_after_reassignment", "allow_overtime",
]);

function normalizeValidationErrors(value) {
  return (Array.isArray(value) ? value : []).slice(0, 10).flatMap((item) => {
    if (!isPlainObject(item) || typeof item.field !== "string" || !item.field || typeof item.code !== "string"
      || !/^[a-z][a-z0-9_]{0,79}$/.test(item.code)) return [];
    const field = item.field.split(".").slice(0, 8)
      .map((part) => VALIDATION_FIELDS.has(part) ? part : /^\d+$/.test(part) || part === "[item]" ? "[item]" : "[field]").join(".");
    return field ? [{ field, code: item.code }] : [];
  });
}

export function extractPlanningValidationErrors(payload) {
  return normalizeValidationErrors((Array.isArray(payload?.detail) ? payload.detail : []).slice(0, 10)
    .map((item) => ({ field: Array.isArray(item?.loc) ? item.loc.join(".") : "", code: item?.type })));
}

function normalizePlanningRevision(value) {
  if (!isPlainObject(value)) return null;
  const normalized = normalizePlanningRevisionEvidence(value);
  for (const key of ["initial_runtime_revision_present", "expected_runtime_revision_present", "expected_assignment_version_present", "result_runtime_revision_present"]) {
    if (typeof value[key] === "boolean") normalized[key] = value[key];
  }
  if (["not_needed", "pending", "succeeded", "failed", "missing_revision", "context_changed", "assignment_changed", "truck_missing", "planning_replaced", "invalid_response"].includes(value.refresh_outcome)) {
    normalized.refresh_outcome = value.refresh_outcome;
  }
  for (const key of ["initial_planning_operation", "last_planning_operation"]) {
    if (typeof value[key] === "string" && /^[a-z][a-z0-9_]{0,119}$/.test(value[key])) normalized[key] = value[key];
  }
  return Object.keys(normalized).length ? normalized : null;
}

export function normalizeDispatchAlertCaptureContext(candidate, defaults = {}) {
  if (!isPlainObject(candidate) && !isPlainObject(defaults)) {
    return null;
  }
  const source = isPlainObject(candidate) ? candidate : {};
  if (
    Object.prototype.hasOwnProperty.call(source, "schema_version")
    && source.schema_version !== DISPATCH_ALERT_CAPTURE_CONTEXT_SCHEMA_VERSION
  ) {
    return null;
  }
  const context = { schema_version: DISPATCH_ALERT_CAPTURE_CONTEXT_SCHEMA_VERSION };
  for (const [key, maxLength] of [
    ["surface", 64],
    ["source", 120],
    ["action", 120],
    ["action_label", 255],
    ["interaction_origin", 64],
    ["operation_id", 255],
  ]) {
    const value = normalizeText(source[key] ?? defaults[key], maxLength);
    if (value) {
      context[key] = value;
    }
  }

  const subjectSource = isPlainObject(source.subject) ? source.subject : {};
  const subject = {
    id: normalizeText(subjectSource.id, 255),
    label: normalizeText(subjectSource.label, 512),
  };
  if (subject.id || subject.label) {
    context.subject = {
      ...(subject.id ? { id: subject.id } : {}),
      ...(subject.label ? { label: subject.label } : {}),
    };
  }

  const entitySource = isPlainObject(source.entities) ? source.entities : {};
  const entities = {};
  for (const key of ["order_ids", "delivery_ids", "loading_step_ids"]) {
    const identifiers = normalizeIdentifierList(entitySource[key]);
    if (identifiers.length) {
      entities[key] = identifiers;
    }
  }
  for (const [key, fallbackCount] of [
    ["order_count", entities.order_ids?.length || 0],
    ["delivery_count", entities.delivery_ids?.length || 0],
  ]) {
    const count = normalizeInteger(entitySource[key]);
    if (count !== null || fallbackCount > 0) {
      entities[key] = count ?? fallbackCount;
    }
  }
  for (const key of ["source_truck", "target_truck", "current_truck"]) {
    const truck = normalizeTruck(entitySource[key]);
    if (truck) {
      entities[key] = truck;
    }
  }
  if (Object.keys(entities).length) {
    context.entities = entities;
  }

  const dispatchSource = isPlainObject(source.dispatch) ? source.dispatch : {};
  const dispatch = {};
  for (const [key, maxLength] of [
    ["rebuild_mode", 32],
    ["source_date", 32],
    ["target_date", 32],
    ["target_period", 32],
  ]) {
    const value = normalizeText(dispatchSource[key], maxLength);
    if (value) {
      dispatch[key] = value;
    }
  }
  for (const key of ["protect_after_reassignment", "allow_overtime"]) {
    if (typeof dispatchSource[key] === "boolean") {
      dispatch[key] = dispatchSource[key];
    }
  }
  if (Object.keys(dispatch).length) {
    context.dispatch = dispatch;
  }

  const technicalSource = isPlainObject(source.technical) ? source.technical : {};
  const technical = {};
  for (const [key, maxLength] of [
    ["method", 16],
    ["path", 512],
    ["reason_code", 255],
    ["failure_kind", 64],
    ["correlation_id", 255],
    ["failed_stage", 255],
  ]) {
    const value = key === "path"
      ? normalizePath(technicalSource[key])
      : normalizeText(technicalSource[key], maxLength);
    if (value) {
      technical[key] = key === "method" ? value.toUpperCase() : value;
    }
  }
  const httpStatus = normalizeInteger(technicalSource.http_status, { min: 100, max: 599 });
  if (httpStatus !== null) {
    technical.http_status = httpStatus;
  }
  if (Object.keys(technical).length) {
    context.technical = technical;
  }

  const serverTransport = normalizeServerResponseTransport(source.server_response_transport);
  if (serverTransport) context.server_response_transport = serverTransport;
  if (["available", "not_found", "lookup_failed"].includes(source.server_transport_state)) context.server_transport_state = source.server_transport_state;
  const mutationDiagnostic = normalizeCapturedMutationDiagnostic(source.mutation_diagnostic);
  if (mutationDiagnostic) context.mutation_diagnostic = mutationDiagnostic;
  const mutationTransport = normalizeMutationTransport(source.mutation_transport);
  if (mutationTransport) context.mutation_transport = mutationTransport;
  const validationErrors = normalizeValidationErrors(source.validation_errors);
  if (validationErrors.length) context.validation_errors = validationErrors;
  const planningRevision = normalizePlanningRevision(source.planning_revision);
  if (planningRevision) context.planning_revision = planningRevision;
  const orderEdit = normalizeOrderEditCapture(source.order_edit);
  if (orderEdit) context.order_edit = orderEdit;
  const details = normalizeDetails(source.details);
  if (details.length) {
    context.details = details;
  }
  return Object.keys(context).length > 1 ? fitContextToByteLimit(context) : null;
}

export function serializeDispatchAlertCaptureContext(candidate, defaults = {}) {
  const context = normalizeDispatchAlertCaptureContext(candidate, defaults);
  return context ? JSON.stringify(context) : "";
}

export function parseDispatchAlertCaptureContext(value, defaults = {}) {
  if (isPlainObject(value)) {
    return normalizeDispatchAlertCaptureContext(value, defaults);
  }
  const serialized = normalizeText(value, DISPATCH_ALERT_CAPTURE_CONTEXT_MAX_BYTES * 2);
  if (!serialized || jsonByteLength(serialized) > DISPATCH_ALERT_CAPTURE_CONTEXT_MAX_BYTES) {
    return null;
  }
  try {
    return normalizeDispatchAlertCaptureContext(JSON.parse(serialized), defaults);
  } catch {
    return null;
  }
}

export function mergeDispatchAlertCaptureContext(base, updates = {}) {
  const left = normalizeDispatchAlertCaptureContext(base) || {};
  const right = isPlainObject(updates) ? updates : {};
  return normalizeDispatchAlertCaptureContext({
    ...left,
    ...right,
    subject: { ...(left.subject || {}), ...(right.subject || {}) },
    entities: { ...(left.entities || {}), ...(right.entities || {}) },
    dispatch: { ...(left.dispatch || {}), ...(right.dispatch || {}) },
    technical: { ...(left.technical || {}), ...(right.technical || {}) },
    details: [...(left.details || []), ...(right.details || [])],
  });
}

function resolveTruckLabel(state, truckId, fallbackLabel = "") {
  const id = normalizeText(truckId, 255);
  const fallback = normalizeText(fallbackLabel, 255);
  if (!id) {
    return fallback;
  }
  const mapped = normalizeText(state?.assignmentsById?.get?.(id)?.truck_name, 255);
  if (mapped) {
    return mapped;
  }
  const assignment = Array.isArray(state?.resultsData?.assignments)
    ? state.resultsData.assignments.find((item) => normalizeText(item?.truck_id, 255) === id)
    : null;
  return normalizeText(assignment?.truck_name, 255) || fallback || id;
}

function resolveDeliveryItem(state, deliveryId) {
  const id = normalizeText(deliveryId, 255);
  if (!id) {
    return null;
  }
  const mapped = state?.deliveryItemsByOrderId?.get?.(id);
  if (isPlainObject(mapped)) {
    return mapped;
  }
  return Array.isArray(state?.resultsData?.suspended_deliveries)
    ? state.resultsData.suspended_deliveries.find((item) => normalizeText(item?.delivery_id, 255) === id) || null
    : null;
}

function buildTruck(id, state, fallbackLabel = "") {
  const normalizedId = normalizeText(id, 255);
  const label = resolveTruckLabel(state, normalizedId, fallbackLabel);
  return normalizedId || label
    ? { ...(normalizedId ? { id: normalizedId } : {}), ...(label ? { label } : {}) }
    : null;
}

function buildSegmentReorderCapture(requestBody, diagnostics) {
  const request = isPlainObject(diagnostics?.segment_reorder_request)
    ? diagnostics.segment_reorder_request
    : {};
  const movedStepIds = normalizeIdentifierList(request.dragged_step_ids, {
    additional: [requestBody.draggedStepId, ...(Array.isArray(requestBody.draggedStepIds) ? requestBody.draggedStepIds : [])],
  });
  const movedEntityIds = normalizeIdentifierList(request.moved_entity_ids);
  const targetStepId = normalizeText(request.target_step_id ?? requestBody.targetStepId, 255);
  const targetEntityId = normalizeText(request.target_entity_id, 255);
  const scope = normalizeText(request.scope ?? requestBody.scope, 64);
  const placement = normalizeText(request.placement ?? requestBody.placement, 32);
  const localDetails = isPlainObject(diagnostics?.local_patch?.details) ? diagnostics.local_patch.details : {};
  const strictDetails = isPlainObject(diagnostics?.delivery_block_strict_replay) ? diagnostics.delivery_block_strict_replay : {};
  const scopeLabel = {
    loads: "Chargements du camion",
    "deliveries-with-same-parent-load": "Livraisons du même chargement",
    "delivery-block": "Livraisons du même bloc",
  }[scope] || scope;
  const placementLabel = placement === "after"
    ? "après la cible"
    : placement === "before"
      ? "avant la cible"
      : placement;
  const sequenceBefore = normalizeIdentifierList(request.sequence_before);
  const sequenceRequested = normalizeIdentifierList(request.sequence_requested);
  const reasonDetail = normalizeText(localDetails.requested_target_rejection_detail ?? strictDetails.requested_target_rejection_detail, 1024);
  return {
    entityKind: normalizeText(request.entity_kind, 32),
    movedEntityIds,
    movedStepIds,
    targetEntityId,
    truckId: normalizeText(request.truck_id ?? requestBody.truckId, 255),
    details: [
      scopeLabel ? { key: "segment-reorder-scope", label: "Portée", value: scopeLabel } : null,
      (movedStepIds.length || targetStepId) ? {
        key: "segment-reorder-steps",
        label: "Segments demandés",
        value: [[movedStepIds.join(", "), targetStepId].filter(Boolean).join(" → "), placementLabel].filter(Boolean).join(" · "),
      } : null,
      (movedEntityIds.length || targetEntityId) ? {
        key: "segment-reorder-entities",
        label: request.entity_kind === "delivery" ? "Livraisons" : "Commandes",
        value: [movedEntityIds.join(", "), targetEntityId].filter(Boolean).join(" → "),
      } : null,
      sequenceBefore.length ? { key: "segment-reorder-sequence-before", label: "Séquence avant", value: sequenceBefore.join(" → ") } : null,
      sequenceRequested.length ? { key: "segment-reorder-sequence-requested", label: "Séquence demandée", value: sequenceRequested.join(" → ") } : null,
      reasonDetail ? { key: "segment-reorder-reason-detail", label: "Cause détaillée", value: reasonDetail } : null,
    ].filter(Boolean),
  };
}

export function buildPlanningAlertCaptureContext({
  actionLabel = "",
  actionName = "mutation",
  body = null,
  correlationId = "",
  deliveryId = "",
  detailItems = [],
  failureKind = "",
  httpStatus = null,
  interactionOrigin = "direct",
  loadingStepId = "",
  method = "POST",
  operationId = "",
  orderId = "",
  path = "",
  planningDiagnostics = null,
  mutationTransport = null,
  validationErrors = [],
  planningRevision = null,
  orderEditSubmission = null,
  reasonCode = "",
  state = null,
  subjectLabel = "",
} = {}) {
  const requestBody = isPlainObject(body) ? body : {};
  const diagnostics = isPlainObject(planningDiagnostics) ? planningDiagnostics : {};
  const isSegmentReorder = normalizeText(actionName, 120) === "segment_reorder";
  const segmentReorderRequest = isSegmentReorder
    ? buildSegmentReorderCapture(requestBody, diagnostics)
    : null;
  const segmentReorderOrderIds = segmentReorderRequest?.entityKind === "order"
    ? [...segmentReorderRequest.movedEntityIds, segmentReorderRequest.targetEntityId]
    : [];
  const segmentReorderDeliveryIds = segmentReorderRequest?.entityKind === "delivery"
    ? [...segmentReorderRequest.movedEntityIds, segmentReorderRequest.targetEntityId]
    : [];
  const orderIds = collectIdentifiersByKeys(requestBody, ORDER_ID_KEYS, [orderId, ...segmentReorderOrderIds]);
  const deliveryIds = collectIdentifiersByKeys(requestBody, DELIVERY_ID_KEYS, [deliveryId, ...segmentReorderDeliveryIds]);
  const loadingStepIds = collectIdentifiersByKeys(requestBody, LOADING_STEP_ID_KEYS, loadingStepId);
  const primaryDelivery = resolveDeliveryItem(state, deliveryIds.values[0]);
  const primaryOrderId = normalizeText(primaryDelivery?.order_id, 255);
  if (primaryOrderId && !orderIds.values.includes(primaryOrderId)) {
    orderIds.values.unshift(primaryOrderId);
    orderIds.count = Math.max(orderIds.count, orderIds.values.length);
  }

  const sourceTruckId = normalizeText(requestBody.source_truck_id, 255)
    || normalizeText(primaryDelivery?.current_truck_id, 255)
    || findFirstNestedText(diagnostics, ["source_truck_id", "source_rebuild_truck_id"]);
  const targetTruckId = normalizeText(requestBody.target_truck_id, 255)
    || findFirstNestedText(diagnostics, ["target_truck_id", "requested_target_truck_id"]);
  const currentTruckId = normalizeText(requestBody.current_truck_id ?? requestBody.truck_id ?? requestBody.truckId, 255)
    || segmentReorderRequest?.truckId
    || findFirstNestedText(diagnostics, ["current_truck_id", "truck_id"]);
  const sourceTruckLabel = findFirstNestedText(diagnostics, ["source_truck_label", "source_truck_name"]);
  const targetTruckLabel = findFirstNestedText(diagnostics, ["target_truck_label", "target_truck_name"]);
  const currentTruckLabel = findFirstNestedText(diagnostics, ["current_truck_label", "current_truck_name", "truck_label", "truck_name"]);
  const resolvedSubjectLabel = normalizeText(subjectLabel, 512)
    || normalizeText(primaryDelivery?.order_client_name ?? primaryDelivery?.client_name, 512)
    || orderIds.values[0]
    || deliveryIds.values[0]
    || loadingStepIds.values[0]
    || segmentReorderRequest?.movedStepIds[0];
  const hasReassignmentOptions = Boolean(normalizeText(requestBody.rebuild_mode, 32));
  const failedStage = findFirstNestedText(diagnostics, ["failed_stage", "cancelled_stage", "reason_stage"]);
  const diagnosticReasonCode = resolveDiagnosticFailureReason(diagnostics);

  return normalizeDispatchAlertCaptureContext({
    schema_version: DISPATCH_ALERT_CAPTURE_CONTEXT_SCHEMA_VERSION,
    source: "planning",
    action: normalizeText(actionName, 120),
    action_label: normalizeText(actionLabel, 255),
    interaction_origin: normalizeText(interactionOrigin, 64),
    operation_id: normalizeText(operationId || correlationId, 255),
    subject: resolvedSubjectLabel
      ? { id: orderIds.values[0] || deliveryIds.values[0] || loadingStepIds.values[0], label: resolvedSubjectLabel }
      : null,
    entities: {
      order_ids: orderIds.values,
      delivery_ids: deliveryIds.values,
      loading_step_ids: loadingStepIds.values,
      order_count: orderIds.count,
      delivery_count: deliveryIds.count,
      source_truck: buildTruck(sourceTruckId, state, sourceTruckLabel),
      target_truck: buildTruck(targetTruckId, state, targetTruckLabel),
      current_truck: buildTruck(currentTruckId, state, currentTruckLabel),
    },
    dispatch: {
      rebuild_mode: normalizeText(requestBody.rebuild_mode, 32),
      ...(hasReassignmentOptions
        ? { protect_after_reassignment: requestBody.protect_after_reassignment !== false }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(requestBody, "allow_overtime")
        ? { allow_overtime: requestBody.allow_overtime === true }
        : {}),
      source_date: normalizeText(requestBody.source_dispatch_date ?? requestBody.dispatch_date, 32),
      target_date: normalizeText(requestBody.target_dispatch_date, 32),
      target_period: normalizeText(requestBody.target_period ?? requestBody.dispatch_period, 32),
    },
    technical: {
      method,
      path,
      http_status: httpStatus,
      reason_code: normalizeText(reasonCode, 255) || diagnosticReasonCode,
      failure_kind: normalizeText(failureKind, 64),
      correlation_id: normalizeText(correlationId, 255),
      failed_stage: failedStage,
    },
    ...(mutationTransport ? { mutation_transport: mutationTransport } : {}),
    validation_errors: validationErrors,
    planning_revision: planningRevision,
    ...(actionName === "gantt_source_backed_save" ? { order_edit: {
      submission: orderEditSubmission,
      canonical: diagnostics.order_edit_details,
      failure: diagnostics.order_edit_failure,
    } } : {}),
    details: [
      ...(segmentReorderRequest?.details || []),
      ...(Array.isArray(detailItems) ? detailItems : []),
    ],
  });
}

function resolveDiagnosticFailureReason(diagnostics, explicitReason = "") {
  const terminalReason = normalizeText(diagnostics?.reason_code, 255);
  if (terminalReason) {
    return terminalReason;
  }
  const localPatch = isPlainObject(diagnostics?.local_patch) ? diagnostics.local_patch : {};
  const localPatchDetails = isPlainObject(localPatch.details) ? localPatch.details : {};
  const preciseLocalReason = normalizeText(localPatchDetails.requested_target_rejection_code, 255);
  if (preciseLocalReason) {
    return preciseLocalReason;
  }
  const localPatchReason = normalizeText(localPatch.reason, 255);
  if (localPatchReason) {
    return localPatchReason;
  }
  // Final rejections explain the failure; earlier successful guards also carry
  // reason codes and must not hide the actual constraint that refused saving.
  for (const key of ["final_operational_guard", "hard_time_window_guard", "hard_eligibility_guard", "protected_membership_guard", "protected_order_capacity_guard", "excluded_truck_refresh"]) {
    const guard = diagnostics?.[key];
    if (/reject|failed|warning/.test(guard?.status || "")) {
      const reason = findFirstNestedText(guard, ["reason_code", "error_code", "code"]);
      if (reason) return reason;
    }
  }
  // Le code explicite du refus passe avant la recherche générique, qui ignore les
  // contrôles n'ayant rien refusé (« passed », « not_applied », « skipped »…).
  const unresolvedDiagnostics = Object.fromEntries(Object.entries(isPlainObject(diagnostics) ? diagnostics : {})
    .filter(([, value]) => !(typeof value?.status === "string" && !/reject|failed|warning/.test(value.status))));
  return normalizeText(explicitReason, 255) || findFirstNestedText(unresolvedDiagnostics, ["reason_code", "error_code", "code"]);
}

export function buildStatusNoticeAlertCaptureContext(candidate = {}) {
  if (!isPlainObject(candidate)) {
    return null;
  }
  const diagnostics = candidate.optimizationDiagnostics
    || candidate.optimization_diagnostics
    || candidate.planningDiagnostics
    || candidate._planning_diagnostics
    || candidate.diagnostics
    || null;
  const base = normalizeDispatchAlertCaptureContext(candidate.captureContext) || {};
  const source = normalizeText(candidate.source, 120) || base.source || "status_notice";
  const action = normalizeText(candidate.action ?? candidate.operationKind ?? candidate.operation_kind, 120)
    || base.action
    || findFirstNestedText(diagnostics, ["action", "operation_kind", "operation_action"]);
  const operationId = normalizeText(candidate.operationId ?? candidate.operation_id ?? candidate.jobId ?? candidate.job_id, 255)
    || base.operation_id;
  const diagnosticReasonCode = resolveDiagnosticFailureReason(diagnostics, base.technical?.reason_code);
  const diagnosticFailedStage = findFirstNestedText(diagnostics, ["failed_stage", "cancelled_stage", "reason_stage"]);
  const issueItems = Array.isArray(candidate.issueItems) ? candidate.issueItems : [];
  const issueOrderIds = normalizeIdentifierList(issueItems.map((item) => item?.orderId ?? item?.order_id));
  const detailItems = [
    ...(Array.isArray(candidate.detailItems) ? candidate.detailItems : []),
    ...issueItems.map((item, index) => ({
      key: normalizeText(item?.key, 120) || `issue-${index + 1}`,
      label: normalizeText(item?.orderId ?? item?.order_id, 160) || `Anomalie ${index + 1}`,
      value: normalizeText(item?.summary, 1024) || normalizeText(item?.title, 1024),
    })),
  ];
  return mergeDispatchAlertCaptureContext(base, {
    surface: "status_notice",
    source,
    action,
    action_label: normalizeText(candidate.actionLabel ?? candidate.action_label, 255) || base.action_label,
    operation_id: operationId,
    subject: {
      ...(base.subject || {}),
      label: base.subject?.label || normalizeText(candidate.title, 512),
    },
    entities: {
      ...(base.entities || {}),
      ...(issueOrderIds.length
        ? { order_count: issueOrderIds.length, order_ids: issueOrderIds }
        : {}),
    },
    dispatch: {
      ...(base.dispatch || {}),
      target_date: normalizeText(candidate.dispatchDate ?? candidate.dispatch_date, 32) || base.dispatch?.target_date,
      target_period: normalizeText(candidate.dispatchPeriod ?? candidate.dispatch_period, 32) || base.dispatch?.target_period,
    },
    technical: {
      ...(base.technical || {}),
      reason_code: diagnosticReasonCode || base.technical?.reason_code,
      failed_stage: diagnosticFailedStage || base.technical?.failed_stage,
      failure_kind: candidate.status === "failed"
        ? (base.technical?.failure_kind || "status_notice")
        : base.technical?.failure_kind,
      correlation_id: base.technical?.correlation_id || operationId,
    },
    details: detailItems,
  });
}

export function buildNonComplianceAlertCaptureContext(messages = []) {
  const details = (Array.isArray(messages) ? messages : [])
    .map((message, index) => ({
      key: `non-compliance-${index + 1}`,
      label: `Anomalie ${index + 1}`,
      value: normalizeText(message, 1024),
    }));
  return normalizeDispatchAlertCaptureContext({
    schema_version: DISPATCH_ALERT_CAPTURE_CONTEXT_SCHEMA_VERSION,
    surface: "non_compliance",
    source: "fleet_configuration",
    action: "truck_configuration_validation",
    action_label: "Validation de configuration camion",
    subject: { label: "Certains camions sont non conformes" },
    technical: { failure_kind: "validation" },
    details,
  });
}

export function resolveDispatchAlertCaptureOperationKey(context) {
  const normalized = normalizeDispatchAlertCaptureContext(context);
  if (!normalized) {
    return "";
  }
  if (normalized.operation_id) {
    return `operation:${normalized.operation_id}`;
  }
  const entities = normalized.entities || {};
  return [
    normalized.action || "",
    entities.source_truck?.id || entities.source_truck?.label || "",
    entities.target_truck?.id || entities.target_truck?.label || "",
    ...(entities.order_ids || []),
    ...(entities.delivery_ids || []),
  ].filter(Boolean).join("|");
}

export function scoreDispatchAlertCaptureContext(context) {
  const normalized = normalizeDispatchAlertCaptureContext(context);
  if (!normalized) {
    return 0;
  }
  const entities = normalized.entities || {};
  const trucks = [entities.source_truck, entities.target_truck, entities.current_truck].filter(Boolean);
  return [
    normalized.surface,
    normalized.source,
    normalized.action,
    normalized.action_label,
    normalized.interaction_origin,
    normalized.operation_id,
  ].filter(Boolean).length
    + Object.keys(normalized.subject || {}).length
    + (entities.order_ids?.length || 0)
    + (entities.delivery_ids?.length || 0)
    + (entities.loading_step_ids?.length || 0)
    + [entities.order_count, entities.delivery_count].filter((value) => Number.isInteger(value)).length
    + trucks.reduce((score, truck) => score + Object.keys(truck).length, 0)
    + Object.keys(normalized.dispatch || {}).length
    + Object.keys(normalized.technical || {}).length
    + (normalized.validation_errors?.length || 0) * 2
    + Object.keys(normalized.planning_revision || {}).length
    + (normalized.mutation_transport?.response_received ? 10 : 0)
    + ((normalized.details?.length || 0) * 2)
    + (normalized.order_edit?.canonical ? 20 : 0)
    + (normalized.order_edit?.submission ? 10 : 0);
}
